import React from 'react';
import { walkClaimText } from '../../../../lib/walkClaimText';
import type { SiteRagReport } from '../../../../types/siteRag';
import { SectionShell } from '../SectionShell';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import { walkCitationText } from '../../../../lib/walkCitationText';
import { EditorialSubsection, EditorialDataTable, EditorialCallout } from '../../report/editorial';
import { resolveSectionCallout } from '../../../../lib/reportRendering/reportSectionCallouts';
import { WithheldNarrativeNotice } from '../../report/WithheldNarrativeNotice';
import { isWithheldNarrativeNotice } from '../../../../lib/reportRendering/scrubInternalSentinels';

/**
 * Consumptive-use / withdrawal PERMITTING gate (R-BTM-DILIGENCE). The Gemini
 * schema emits an OPTIONAL `consumptiveUse` sub-object alongside the
 * waterRightsAnalysis prose (the prompt folds the same content into both).
 * The four narrative gate fields render here as a 2-column journal table
 * (Gate × Assessment) so the binding-gate signal is scannable rather than
 * buried in prose. `dryCoolingPueTradeoff` is intentionally NOT rendered here
 * — it is topically a cooling↔PUE tradeoff and renders in CoolingByTechSection.
 *
 * Citation-walked (walkCitationText) so inline [Ref N] tokens stay live, and
 * the gate's presence is OR'd into the section hasData so a structured-only
 * payload (gate present, prose empty) still shows the section instead of the
 * insufficient-data placeholder.
 */
const CONSUMPTIVE_USE_GATE_ROWS: { key: 'doctrineGate' | 'withdrawalPermit' | 'consumptiveVolume' | 'seniorRightCurtailment'; gate: string }[] = [
  { key: 'doctrineGate', gate: 'Doctrine gate' },
  { key: 'withdrawalPermit', gate: 'Withdrawal permit' },
  { key: 'consumptiveVolume', gate: 'Consumptive volume' },
  { key: 'seniorRightCurtailment', gate: 'Senior-right curtailment' },
];

export const WaterRightsSection: React.FC<{ report: SiteRagReport; label: string }> = ({ report, label }) => {
  const theme = useEffectiveReportTheme();
  const text = report.waterAnalysis?.waterRightsAnalysis;
  const cu = report.waterAnalysis?.consumptiveUse;
  const gateRows = CONSUMPTIVE_USE_GATE_ROWS
    .filter((r) => !!cu?.[r.key]?.trim())
    .map((r) => [r.gate, walkCitationText(cu![r.key], report.references)] as React.ReactNode[]);
  const hasGate = gateRows.length > 0;
  const callout = resolveSectionCallout(report, 'waterRights');
  // A withheld rights narrative is a GATING gap: keep the section visible and
  // show it as open ("not assessed"), never as prose and never dropped.
  const withheld = isWithheldNarrativeNotice(text);
  return (
    <SectionShell label={label} hasData={!!text || hasGate}>
      {text && withheld && (
        <WithheldNarrativeNotice variant="report" theme={theme} />
      )}
      {text && !withheld && (
        <EditorialSubsection theme={theme} label="Rights & Allocation">
          {walkClaimText(text, report.references)}
        </EditorialSubsection>
      )}
      {hasGate && (
        <EditorialSubsection theme={theme} label="Consumptive-Use / Withdrawal Gate">
          {/* data-span-columns: the overflow-x-auto wrapper is a BFC that would otherwise trap this
              table in a single flow column (crushed to half-width in the 2-col presets). Latent
              until now because the table is behind `hasGate` — the visual-matrix fixture carries no
              consumptive-use gate, so the runtime span-hook check never rendered it. */}
          <div data-span-columns className="overflow-x-auto" data-testid="consumptive-use-gate">
            <EditorialDataTable
              theme={theme}
              columns={[{ header: 'Gate' }, { header: 'Assessment' }]}
              rows={gateRows}
            />
          </div>
        </EditorialSubsection>
      )}
      {callout && (
        <EditorialCallout theme={theme} tone={callout.tone} label={callout.label} style={{ marginTop: 8 }}>
          {callout.body}
        </EditorialCallout>
      )}
    </SectionShell>
  );
};
